import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Copy, Search, Zap } from "lucide-react";
import { useShortcodeProcessor } from "@/hooks/useShortcodeProcessor";
import { useBrandManager } from "@/hooks/useBrandManager";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface ShortcodePopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInsert?: (shortcode: string) => void;
}

export const ShortcodePopup: React.FC<ShortcodePopupProps> = ({
  open,
  onOpenChange,
  onInsert
}) => {
  const { availablePersonas, availablePersonaBriefs } = useShortcodeProcessor();
  const { currentBrandId } = useBrandManager();
  const [assets, setAssets] = useState<any[]>([]);
  const [collaborationTypes, setCollaborationTypes] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Define static shortcodes by category
  const staticShortcodes = {
    "brand-data": [
      { shortcode: "{brand_name}", description: "Your brand name" },
      { shortcode: "{description}", description: "Brand description" },
      { shortcode: "{website}", description: "Brand website URL" },
      { shortcode: "{competitors}", description: "List of competitor brands" },
      { shortcode: "{personas}", description: "List of brand personas" },
      { shortcode: "{prompts}", description: "List of brand prompts" },
      { shortcode: "{seed_keywords}", description: "Seed keywords for the brand" },
      { shortcode: "{targeted_countries}", description: "Target countries for the brand" },
      { shortcode: "{seed_hashtags}", description: "Social media hashtags" },
      { shortcode: "{brand_hashtags}", description: "Brand-specific hashtags" },
      { shortcode: "{languages}", description: "Target languages for the brand" },
      { shortcode: "{important_pages}", description: "Important pages for the brand" },
    ],
    "social-media": [
      { shortcode: "{youtube}", description: "YouTube profile URL" },
      { shortcode: "{instagram}", description: "Instagram profile URL" },
      { shortcode: "{facebook}", description: "Facebook profile URL" },
      { shortcode: "{tiktok}", description: "TikTok profile URL" },
      { shortcode: "{x}", description: "X/Twitter profile URL" },
      { shortcode: "{linkedin}", description: "LinkedIn profile URL" },
    ],
    "utilities": [
      { shortcode: "{today}", description: "Current date" },
      { shortcode: "{user:name}", description: "Current user's name" },
    ],
    "collaborators": [
      // Basic Info
      { shortcode: "{collaborator_name}", description: "Collaborator's full name" },
      { shortcode: "{collaborator_firstName}", description: "Collaborator's first name" },
      { shortcode: "{collaborator_lastName}", description: "Collaborator's last name" },
      { shortcode: "{collaborator_email}", description: "Collaborator's email address" },
      { shortcode: "{collaborator_phone}", description: "Collaborator's phone number" },
      { shortcode: "{collaborator_company}", description: "Collaborator's company name" },
      { shortcode: "{collaborator_job_title}", description: "Collaborator's job title/position" },
      { shortcode: "{collaborator_location}", description: "Collaborator's location" },
      // Social & Web
      { shortcode: "{collaborator_website}", description: "Collaborator's website URL" },
      { shortcode: "{collaborator_linkedin}", description: "Collaborator's LinkedIn profile URL" },
      { shortcode: "{collaborator_twitter_handle}", description: "Collaborator's Twitter/X handle" },
      // Source Attribution
      { shortcode: "{collaborator_source}", description: "Lead source (how they were acquired)" },
      { shortcode: "{collaborator_medium}", description: "Marketing medium" },
      { shortcode: "{collaborator_channel}", description: "Marketing channel" },
      { shortcode: "{collaborator_metric_type}", description: "Metric type (leads, sales, etc.)" },
      // Assigned Types
      { shortcode: "{collaborator_types_list}", description: "All collaboration types assigned" },
      { shortcode: "{collaborator_primary_type}", description: "Primary collaboration type name" },
      { shortcode: "{collaborator_primary_type_description}", description: "Description of primary type" },
      // AI Opportunities
      { shortcode: "{collaborator_opportunities_list}", description: "All AI-identified opportunities" },
      { shortcode: "{collaborator_primary_opportunity}", description: "Highest confidence opportunity" },
      { shortcode: "{collaborator_primary_opportunity_description}", description: "Description of best opportunity" },
      { shortcode: "{collaborator_primary_opportunity_next_steps}", description: "Next steps for best opportunity" },
      // Enrichment & Other
      { shortcode: "{collaborator_enriched_company_info}", description: "AI-enriched company info" },
      { shortcode: "{collaborator_enriched_linkedin_info}", description: "AI-enriched LinkedIn data" },
      { shortcode: "{collaborator_insights}", description: "Stored insights" },
      { shortcode: "{collaborator_remarks}", description: "Notes and remarks" },
      { shortcode: "{collaborator_status}", description: "Current status" },
    ]
  };

  // Fetch brand assets and collaboration types
  useEffect(() => {
    const fetchData = async () => {
      if (!currentBrandId) return;
      
      try {
        // Fetch brand assets
        const { data: assetsData, error: assetsError } = await supabase
          .from('brand_assets')
          .select('*')
          .eq('brand_id', currentBrandId)
          .order('sort_order', { ascending: true });

        if (assetsError) throw assetsError;
        setAssets(assetsData || []);

        // Fetch collaboration types
        const { data: collabData, error: collabError } = await supabase
          .from('os_collabs_types')
          .select('*')
          .eq('brand_id', currentBrandId)
          .eq('status', 'active')
          .order('name');

        if (collabError) throw collabError;
        setCollaborationTypes(collabData || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    if (open) {
      fetchData();
    }
  }, [currentBrandId, open]);

  const handleShortcodeClick = async (text: string) => {
    if (onInsert) {
      onInsert(text);
    } else {
      try {
        await navigator.clipboard.writeText(text);
        toast.success(`Shortcode "${text}" copied to clipboard`);
      } catch (error) {
        toast.error("Unable to copy to clipboard");
      }
    }
  };

  // Build dynamic shortcodes from data
  const buildPersonasShortcodes = () => {
    const result: any[] = [];
    availablePersonas.forEach((persona) => {
      result.push(
        { shortcode: `{persona:${persona.name}}`, description: `Brief overview of ${persona.displayName}`, format: "brief" },
        { shortcode: `{persona:${persona.name}:detailed}`, description: `Detailed information about ${persona.displayName}`, format: "detailed" },
        { shortcode: `{persona:${persona.name}:full}`, description: `Complete persona profile for ${persona.displayName}`, format: "full" }
      );
    });
    return result;
  };

  const buildPersonaBriefsShortcodes = () => {
    const result: any[] = [];
    availablePersonaBriefs.forEach((brief) => {
      result.push(
        { shortcode: `{personabrief:${brief.name}}`, description: `Generated brief for ${brief.displayName}`, format: "brief" },
        { shortcode: `{personabrief:${brief.name}:detailed}`, description: `Detailed brief content for ${brief.displayName}`, format: "detailed" }
      );
    });
    return result;
  };

  const buildBrandAssetsShortcodes = () => {
    return assets.map((asset) => ({
      shortcode: `{asset:${asset.variable_name}}`,
      description: `${asset.name} - ${asset.asset_type}`
    }));
  };

  const buildCollaborationTypesShortcodes = () => {
    const result: any[] = [
      { shortcode: '{collab_type:list}', description: 'List of all active collaboration types' },
      { shortcode: '{collab_type:id:name}', description: 'Dynamic: Name by UUID' },
      { shortcode: '{collab_type:id:description}', description: 'Dynamic: Description by UUID' }
    ];
    collaborationTypes.forEach((type) => {
      result.push(
        { shortcode: `{collab_type:${type.name}:description}`, description: `Description of ${type.name}` }
      );
    });
    return result;
  };

  // Get all shortcodes
  const getAllShortcodes = () => {
    const allShortcodes = [
      ...staticShortcodes["collaborators"],
      ...buildPersonasShortcodes(),
      ...buildPersonaBriefsShortcodes(),
      ...buildCollaborationTypesShortcodes(),
      ...buildBrandAssetsShortcodes(),
      ...staticShortcodes["brand-data"],
      ...staticShortcodes["social-media"],
      ...staticShortcodes["utilities"]
    ];

    if (searchTerm) {
      return allShortcodes.filter(item => 
        item.shortcode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return allShortcodes;
  };

  const ShortcodeItem = ({ shortcode, description, format }: { shortcode: string; description: string; format?: string }) => (
    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <code className="font-mono text-sm bg-muted px-2 py-1 rounded">{shortcode}</code>
          {format && <Badge variant="outline" className="text-xs">{format}</Badge>}
        </div>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleShortcodeClick(shortcode)}
        className="ml-2"
      >
        <Copy className="h-4 w-4" />
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-6">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Available Shortcodes
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 flex flex-col gap-4 min-h-0 mt-4">
          {/* Search */}
          <div className="relative flex-shrink-0">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search shortcodes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Shortcodes List */}
          <div className="flex-1 overflow-auto min-h-0">
            <div className="space-y-3 pr-2">
              {getAllShortcodes().length > 0 ? (
                getAllShortcodes().map((item, index) => (
                  <ShortcodeItem
                    key={index}
                    shortcode={item.shortcode}
                    description={item.description}
                    format={item.format}
                  />
                ))
              ) : (
                <p className="text-muted-foreground text-sm text-center py-8">
                  No shortcodes found matching your search.
                </p>
              )}
            </div>
          </div>

          <div className="text-xs text-muted-foreground flex-shrink-0 pt-2 border-t">
            Tip: Click the copy icon to {onInsert ? 'insert' : 'copy'} any shortcode {onInsert ? 'into your prompt' : 'and paste it into your prompt'}.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};